import { CHANNELS, PRIORITIES, RESOLUTIONS, SUBMISSION_TYPES } from '@kinohelpdesk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Bug,
  CheckCircle,
  Copy,
  ExternalLink,
  Github,
  Globe,
  HelpCircle,
  Image,
  Lightbulb,
  type LucideIcon,
  RefreshCw,
  Smartphone,
  Tv,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { type Submission, type SubmissionFilters, submissionsApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatRelativeTime } from '@/lib/utils';

const RESOLUTION_ICONS: Record<string, LucideIcon> = {
  in_progress: RefreshCw,
  resolved: CheckCircle,
  wont_fix: Ban,
  duplicate: Copy,
  invalid: HelpCircle,
};

const CHANNEL_ICONS: Record<string, LucideIcon> = {
  ios: Smartphone,
  tvos: Tv,
  service: Globe,
};

const TYPE_ICONS: Record<string, LucideIcon> = {
  bug_report: Bug,
  feature_request: Lightbulb,
};

export function SubmissionsPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<SubmissionFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['submissions', filters],
    queryFn: () => submissionsApi.list(filters),
  });

  const updateMutation = useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: Partial<Submission> }) =>
      submissionsApi.update(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      setSelectedSubmission(null);
    },
  });

  return (
    <Layout title="Заявки" subtitle={`Всего: ${data?.total ?? 0}`}>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <select
          className="cyber-select py-1.5 text-sm"
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, status: e.target.value || undefined, page: 1 }))
          }
        >
          <option value="">Все статусы</option>
          <option value="pending">Ожидает</option>
          <option value="approved">Одобрено</option>
          <option value="rejected">Отклонено</option>
          <option value="cancelled">Отменено</option>
        </select>
        <select
          className="cyber-select py-1.5 text-sm"
          value={filters.channel ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, channel: e.target.value || undefined, page: 1 }))
          }
        >
          <option value="">Все платформы</option>
          <option value="ios">iOS</option>
          <option value="tvos">tvOS</option>
        </select>
        <select
          className="cyber-select py-1.5 text-sm"
          value={filters.type ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, type: e.target.value || undefined, page: 1 }))
          }
        >
          <option value="">Все типы</option>
          <option value="bug_report">Баг-репорт</option>
          <option value="feature_request">Запрос фичи</option>
        </select>
        <input
          type="text"
          className="cyber-input py-1.5 text-sm flex-1 min-w-[150px]"
          placeholder="Поиск..."
          value={filters.search ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value || undefined, page: 1 }))
          }
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="cyber-card overflow-hidden">
          <table className="cyber-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Пользователь</th>
                <th>Платформа</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Резолюция</th>
                <th>Создана</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.submissions.map((submission) => {
                const ChannelIcon = CHANNEL_ICONS[submission.channel] ?? Globe;
                const TypeIcon = TYPE_ICONS[submission.type] ?? Bug;
                const ResIcon = submission.resolution
                  ? (RESOLUTION_ICONS[submission.resolution] ?? RefreshCw)
                  : null;
                return (
                  <tr key={submission.id}>
                    <td className="font-mono text-neon-cyan">#{submission.ticket_id}</td>
                    <td>
                      <span className="text-gray-300">
                        {submission.telegram_username ? `@${submission.telegram_username}` : submission.telegram_user_id}
                      </span>
                    </td>
                    <td>
                      <span className="flex items-center gap-2">
                        <ChannelIcon className="w-4 h-4 text-gray-400" />
                        {CHANNELS[submission.channel as keyof typeof CHANNELS]?.label}
                      </span>
                    </td>
                    <td>
                      <span className="flex items-center gap-2">
                        <TypeIcon className="w-4 h-4 text-gray-400" />
                        {SUBMISSION_TYPES[submission.type as keyof typeof SUBMISSION_TYPES]?.label}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={submission.status} />
                    </td>
                    <td>
                      {submission.resolution && ResIcon ? (
                        <span className="flex items-center gap-1.5 text-sm text-gray-400">
                          <ResIcon className="w-4 h-4" />
                          {RESOLUTIONS[submission.resolution as keyof typeof RESOLUTIONS]?.label}
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="text-gray-400 text-sm">
                      {formatRelativeTime(submission.created_at)}
                    </td>
                    <td>
                      <button
                        className="cyber-button text-sm"
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        Открыть
                      </button>
                    </td>
                  </tr>
                );
              })}
              {(!data?.submissions || data.submissions.length === 0) && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    Заявки не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {data && data.total > (filters.limit ?? 20) && (
            <div className="flex items-center justify-between p-4 border-t border-cyber-light">
              <span className="text-sm text-gray-400">
                Страница {filters.page ?? 1} из {Math.ceil(data.total / (filters.limit ?? 20))}
              </span>
              <div className="flex gap-2">
                <button
                  className="cyber-button text-sm"
                  disabled={(filters.page ?? 1) <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                >
                  Назад
                </button>
                <button
                  className="cyber-button text-sm"
                  disabled={(filters.page ?? 1) >= Math.ceil(data.total / (filters.limit ?? 20))}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                >
                  Вперёд
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <SubmissionModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onUpdate={(data) =>
            updateMutation.mutate({ ticketId: selectedSubmission.ticket_id, data })
          }
          isUpdating={updateMutation.isPending}
        />
      )}
    </Layout>
  );
}

interface SubmissionModalProps {
  submission: Submission;
  onClose: () => void;
  onUpdate: (data: Partial<Submission>) => void;
  isUpdating: boolean;
}

function SubmissionModal({ submission, onClose, onUpdate, isUpdating }: SubmissionModalProps) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState(submission.admin_comment ?? '');
  const [priority, setPriority] = useState(submission.priority ?? 'medium');
  const [resolution, setResolution] = useState(submission.resolution ?? 'in_progress');
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [githubIssueUrl, setGithubIssueUrl] = useState<string | null>(submission.github_issue_url);

  const ChannelIcon = CHANNEL_ICONS[submission.channel] ?? Globe;
  const TypeIcon = TYPE_ICONS[submission.type] ?? Bug;
  const ResolutionIcon = RESOLUTION_ICONS[resolution] ?? RefreshCw;

  const createGitHubIssueMutation = useMutation({
    mutationFn: () => {
      const typeLabel = SUBMISSION_TYPES[submission.type as keyof typeof SUBMISSION_TYPES]?.label ?? submission.type;
      const title = `[${submission.ticket_id}] ${typeLabel}: ${submission.content.substring(0, 80)}${submission.content.length > 80 ? '...' : ''}`;
      return submissionsApi.createGitHubIssue(submission.ticket_id, { title });
    },
    onSuccess: (data) => {
      setGithubIssueUrl(data.issue_url);
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });

  useEffect(() => {
    if (submission.screenshot_key) {
      submissionsApi.fetchScreenshot(submission.ticket_id).then(setScreenshotUrl);
    }
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    };
  }, [submission.ticket_id, submission.screenshot_key]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="cyber-card w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-4 border-b border-cyber-light">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-display text-white">
              Заявка <span className="text-neon-cyan">#{submission.ticket_id}</span>
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* User info */}
          <div className="flex items-center gap-3 p-3 bg-cyber-gray/50 rounded-lg border border-cyber-light">
            <div className="w-8 h-8 rounded-lg bg-neon-cyan/10 flex items-center justify-center">
              <User className="w-4 h-4 text-neon-cyan" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-200">
                {submission.telegram_username ? `@${submission.telegram_username}` : 'Без username'}
              </p>
              <p className="text-xs text-gray-500">ID: {submission.telegram_user_id}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 uppercase">Платформа</label>
              <p className="flex items-center gap-2 text-sm text-gray-200">
                <ChannelIcon className="w-3.5 h-3.5 text-gray-400" />
                {CHANNELS[submission.channel as keyof typeof CHANNELS]?.label}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Тип</label>
              <p className="flex items-center gap-2 text-sm text-gray-200">
                <TypeIcon className="w-3.5 h-3.5 text-gray-400" />
                {SUBMISSION_TYPES[submission.type as keyof typeof SUBMISSION_TYPES]?.label}
              </p>
            </div>
            {submission.os_version && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Версия ОС</label>
                <p className="text-sm text-gray-200">{submission.os_version}</p>
              </div>
            )}
            {submission.app_version && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Версия приложения</label>
                <p className="text-sm text-gray-200">{submission.app_version}</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase">Описание</label>
            <p className="text-sm text-gray-200 whitespace-pre-wrap mt-1">{submission.content}</p>
          </div>

          {/* Screenshot */}
          {submission.screenshot_key && (
            <div>
              <label className="text-xs text-gray-500 uppercase flex items-center gap-1">
                <Image className="w-3 h-3" /> Скриншот
              </label>
              {screenshotUrl ? (
                <a
                  href={screenshotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-1"
                >
                  <img
                    src={screenshotUrl}
                    alt="Screenshot"
                    className="max-w-full max-h-64 rounded-lg border border-cyber-light hover:border-neon-cyan transition-colors"
                  />
                </a>
              ) : (
                <div className="mt-1 h-32 flex items-center justify-center bg-cyber-gray/50 rounded-lg border border-cyber-light">
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase">Статус</label>
              <div className="mt-1">
                <StatusBadge status={submission.status} />
              </div>
            </div>
            {submission.status === 'approved' && submission.resolution && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Резолюция</label>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-300">
                  <ResolutionIcon className="w-4 h-4" />
                  {RESOLUTIONS[submission.resolution as keyof typeof RESOLUTIONS]?.label}
                </div>
              </div>
            )}
            {submission.priority && (
              <div>
                <label className="text-xs text-gray-500 uppercase">Приоритет</label>
                <div className="mt-1">
                  <StatusBadge status={submission.priority} type="priority" />
                </div>
              </div>
            )}
          </div>

          {/* Priority & Resolution selectors for approved submissions */}
          {submission.status === 'approved' && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-cyber-gray/50 rounded-lg border border-cyber-light">
              <div>
                <label className="text-xs text-gray-500 uppercase mb-1 block">Приоритет</label>
                <select
                  className="cyber-select w-full text-sm"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {Object.entries(PRIORITIES).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase mb-1 block">Резолюция</label>
                <select
                  className="cyber-select w-full text-sm"
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                >
                  {Object.entries(RESOLUTIONS).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Admin comment */}
          <div>
            <label className="text-xs text-gray-500 uppercase">Комментарий модератора</label>
            <textarea
              className="cyber-input mt-1 h-20 resize-none text-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Добавить комментарий..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-cyber-light flex items-center justify-between">
          <div className="flex gap-2">
            {submission.status === 'pending' && (
              <>
                <button
                  className="cyber-button-success flex items-center gap-2 text-sm"
                  onClick={() =>
                    onUpdate({
                      status: 'approved',
                      priority: 'medium',
                      resolution: 'in_progress',
                      admin_comment: comment || undefined,
                    })
                  }
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" /> Одобрить
                    </>
                  )}
                </button>
                <button
                  className="cyber-button-danger flex items-center gap-2 text-sm"
                  onClick={() =>
                    onUpdate({ status: 'rejected', admin_comment: comment || undefined })
                  }
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5" /> Отклонить
                    </>
                  )}
                </button>
              </>
            )}
            {submission.status === 'approved' && (
              <>
                <button
                  className="cyber-button-primary flex items-center gap-2 text-sm"
                  onClick={() =>
                    onUpdate({ priority, resolution, admin_comment: comment || undefined })
                  }
                  disabled={isUpdating}
                >
                  {isUpdating ? <LoadingSpinner size="sm" /> : 'Сохранить'}
                </button>
                {githubIssueUrl ? (
                  <a
                    href={githubIssueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cyber-button flex items-center gap-2 text-sm text-neon-cyan hover:text-white"
                  >
                    <Github className="w-3.5 h-3.5" />
                    GitHub Issue
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <button
                    className="cyber-button flex items-center gap-2 text-sm"
                    onClick={() => createGitHubIssueMutation.mutate()}
                    disabled={createGitHubIssueMutation.isPending}
                  >
                    {createGitHubIssueMutation.isPending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Github className="w-3.5 h-3.5" />
                        Создать Issue
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
          <button className="cyber-button text-sm" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
